import * as Sentry from "@sentry/react";

// Enable Sentry in production and staging builds (never in local development) to avoid noise.
// Set REACT_APP_SENTRY_DSN in the production/staging env.
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const isProductionBuild = process.env.NODE_ENV === "production";
const SENTRY_ENABLED = Boolean(SENTRY_DSN && isProductionBuild);

if (SENTRY_ENABLED) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.REACT_APP_ENV || process.env.NODE_ENV,
    sendDefaultPii: true,
    integrations: [],
  });
}

import "date-fns";

// Remove webpack-dev-server overlay iframe when "Script error" occurs
if (process.env.NODE_ENV === 'development') {
  const removeOverlay = () => {
    const overlay = document.getElementById('webpack-dev-server-client-overlay');
    if (overlay) {
      overlay.remove();
      return true;
    }
    return false;
  };

  // Remove overlay when Script error occurs
  window.addEventListener('error', (event) => {
    if (event.message === 'Script error.' || !event.filename) {
      // Remove overlay immediately and after a short delay (in case it appears later)
      removeOverlay();
      setTimeout(removeOverlay, 50);
      setTimeout(removeOverlay, 200);
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }
  }, true);

  // Watch for overlay creation and remove it if Script error was detected
  let scriptErrorDetected = false;
  window.addEventListener('error', () => {
    scriptErrorDetected = true;
  }, true);

  const observer = new MutationObserver(() => {
    if (scriptErrorDetected) {
      removeOverlay();
    }
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }

  // Also check periodically when Script error was detected
  setInterval(() => {
    if (scriptErrorDetected) {
      removeOverlay();
    }
  }, 200);
}

import React from "react";
import { createRoot } from "react-dom/client";

import App from "./components/App";
import LohonoAuth from "./components/LohonoAuth/LohonoAuth";
import AgentsService from "./core/agent-service";
import ChapterMediaLinkService from "./core/chapter-media-link-service";
import ChapterProjectService from "./core/chapter-project-service";
import ChapterService from "./core/chapter-service";
import ConciergeService from "./core/concierge-service";
import {
  CustomerRelationService,
  CustomerService,
} from "./core/customer-service";
import DashboardService from "./core/dashboard-service";
import DevelopmentMediaLinkService from "./core/development-media-link-service";
import {
  DevelopmentOpportunityGuestService,
  DevelopmentOpportunityService,
} from "./core/development-opportunity-service";
import DevelopmentPriceNoteService from "./core/development-price-note-service";
import DevelopmentProjectService from "./core/development-project-service";
import DropdownOptionsService from "./core/dropdown-options-service";
import { CallService, EnquiryService } from "./core/enquiry-service";
import HOMApprovalService from "./core/hom-approval-service";
import HOMPropertyService from "./core/hom-property-service";
import HomeAcquisitionService from "./core/home-acquisition-service";
import LoginService from "./core/login-service";
import MastersService from "./core/masters-service";
import OutboundOpportunityService from "./core/outbound-opportunity-service";
import PAGE_TEMPLATES from "./core/pages";
import PostSalesInvoiceService from "./core/post-sales-invoice-service";
import RentalCalendarService from "./core/rental-calendar-service";
import RentalCommissionApprovalService from "./core/rental-commission-approval-service";
import RentalCostCentreApprovalService from "./core/rental-cost-centre-approval-service";
import RentalDepositApprovalService from "./core/rental-deposit-approval-service";
import RentalForceBookApprovalService from "./core/rental-force-book-approval-service";
import RentalInvoiceApprovalService from "./core/rental-invoice-approval-service";
import RentalMastersService from "./core/rental-masters-service";
import RentalPaymentApprovalService from "./core/rental-payment-approval-service";
import RentalPropertyService from "./core/rental-property-service";
import RentalTripApprovalService from "./core/rental-trip-approval-service";
import RentalTripPaymentService from "./core/rental-trip-payment-service";
import {
  RentalTripGuestService,
  RentalTripService,
} from "./core/rental-trip-service";
import { SoleneService } from "./core/solene-service";
import StaffService from "./core/staff-service";
import TaskService from "./core/task-service";
import reportWebVitals from "./reportWebVitals";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";

const ROOT_ELEMENT = document.getElementById("root");
const { pathname } = window.location;
if (!ROOT_ELEMENT) {
  throw new Error("not a browser environment");
}
let searchString = window.location.search;
if (searchString.startsWith("?")) {
  searchString = searchString.substring(1);
  if (searchString.split("?").length > 0) {
    const params = searchString
      .split("?");
    searchString = params.length > 0 ? `?${params.join("&")}` : "";
  } else {
    searchString = "";
  }
  window.history.replaceState(
    {},
    document.title,
    `${pathname}${searchString}`.trim(),
  );
}

const intent = `${pathname}${searchString}`.trim(),
  reactRoot = createRoot(ROOT_ELEMENT);

reactRoot.render(
  <LohonoAuth
    apiUrl={`/api/v2/login`}
    authUrl={`https://auth.lohono.com?redirect_url=${window.location.href}`}
  >
    <App
      agentService={AgentsService}
      callService={CallService}
      chapterMediaLinkService={ChapterMediaLinkService}
      chapterProjectService={ChapterProjectService}
      chapterService={ChapterService}
      conciergeService={ConciergeService}
      customerRelationService={CustomerRelationService}
      customerService={CustomerService}
      dashboardService={DashboardService}
      developmentMediaLinkService={DevelopmentMediaLinkService}
      developmentOpportunityGuestService={DevelopmentOpportunityGuestService}
      developmentOpportunityService={DevelopmentOpportunityService}
      developmentPriceNoteService={DevelopmentPriceNoteService}
      developmentProjectService={DevelopmentProjectService}
      dropdownOptionsService={DropdownOptionsService}
      enquiryService={EnquiryService}
      homApprovalService={HOMApprovalService}
      homPropertyService={HOMPropertyService}
      homeAcquisitionService={HomeAcquisitionService}
      intent={intent}
      loginService={LoginService}
      masterService={MastersService}
      outboundOpportunityService={OutboundOpportunityService}
      pageTemplates={PAGE_TEMPLATES}
      postSalesInvoiceService={PostSalesInvoiceService}
      rentalCalendarService={RentalCalendarService}
      rentalCommissionApprovalService={RentalCommissionApprovalService}
      rentalCostCentreApprovalService={RentalCostCentreApprovalService}
      rentalDepositApprovalService={RentalDepositApprovalService}
      rentalForceBookApprovalService={RentalForceBookApprovalService}
      rentalInvoiceService={RentalInvoiceApprovalService}
      rentalMastersService={RentalMastersService}
      rentalPaymentApprovalService={RentalPaymentApprovalService}
      rentalPropertyService={RentalPropertyService}
      rentalTripApprovalService={RentalTripApprovalService}
      rentalTripGuestService={RentalTripGuestService}
      rentalTripPaymentService={RentalTripPaymentService}
      rentalTripService={RentalTripService}
      soleneService={SoleneService}
      staffService={StaffService}
      taskService={TaskService}
    />
  </LohonoAuth>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
